/* eslint-disable @typescript-eslint/explicit-function-return-type */
import type { LocationQuery } from 'vue-router'
import type { Filter, FilterOption } from '~/types/filter.model'
import type {
  PageTrackingData,
  PageLocationTrackingData,
  AppTrackingData,
  InfoCardTrackingData,
  LinkTrackingData,
  CategoryTrackingData,
} from '~/types/tracking'
import { logger } from '~/utils/sentry/client-only-logger'

export const PAGE_EVENT_TIMEOUT_MS = 300
const APP_STORE_EVENT_PREFIX = 'App Store'

export const withPrefix = (eventName: string): string =>
  `${APP_STORE_EVENT_PREFIX} - ${eventName}`

export const getPageEventParams = <T extends PageTrackingData>({
  page,
  pageTitle,
  sourceId,
}: T) => ({
    'Source Page': page,
    'Source Page title': pageTitle,
    'Source id': sourceId,
  })

export const getPageLocationEventParams = <T extends PageLocationTrackingData>({
  pageLocation,
  pageLocationId,
  pageLocationTitle,
}: T) => ({
    'Page location': pageLocation,
    'Page location id': pageLocationId,
    'Page location title': pageLocationTitle,
  })

export const getAppEventParams = <T extends AppTrackingData>({
  appId,
  appTitle,
}: T) => ({
    'App id': appId,
    'App title': appTitle,
  })

export const getInfoCardEventParams = <T extends InfoCardTrackingData>({
  infoCardTitle,
  infoCardButtonTitle,
  infoCardButtonLink,
}: T) => ({
    'Info card title': infoCardTitle,
    'Info card button title': infoCardButtonTitle,
    'Info card button link': infoCardButtonLink,
  })

export const getLinkEventParams = <T extends LinkTrackingData>({
  linkType,
  linkUrl,
  destinationId,
  destinationTitle,
}: T) => ({
    'Link type': linkType,
    'Link URL': linkUrl,
    'Destination id': destinationId,
    'Destination title': destinationTitle,
  })

export const getCategoryEventParams = <T extends CategoryTrackingData>({
  categoryId,
  categoryTitle,
}: T) => ({
    'Category id': categoryId,
    'Category title': categoryTitle,
  })

type FilterAndSearchEventParams = {
  'Search term'?: string | string[]
  'Sorting'?: string | string[]
  'Developer filter ids'?: string | string[]
  'Sendcloud plan filter ids'?: string | string[]
  'Country filter ids'?: string | string[]
  // we also send the titles (names) of chosen options:
  'Developer filter titles'?: string | string[]
  'Sendcloud plan filter titles'?: string | string[]
  'Country filter titles'?: string | string[]
}

type OptionValueToTitleMap = Record<
  FilterOption['value'],
  FilterOption['title']
>

// This map is used to parse queryParams and get filter and search event params
const FILTER_PARSING_MAP: Array<{
  paramName: keyof FilterAndSearchEventParams
  queryKey: string
  shouldMapIdsToTitles?: boolean
  shouldSendArray: boolean
}> = [
  { paramName: 'Search term', queryKey: 'q', shouldSendArray: false },
  { paramName: 'Sorting', queryKey: 'sort', shouldSendArray: false },
  {
    paramName: 'Sendcloud plan filter ids',
    queryKey: 'sendcloud_plan',
    shouldSendArray: true,
  },
  {
    paramName: 'Sendcloud plan filter titles',
    queryKey: 'sendcloud_plan',
    shouldMapIdsToTitles: true,
    shouldSendArray: true,
  },
  {
    paramName: 'Developer filter ids',
    queryKey: 'developers',
    shouldSendArray: true,
  },
  {
    paramName: 'Developer filter titles',
    queryKey: 'developers',
    shouldMapIdsToTitles: true,
    shouldSendArray: true,
  },
  {
    paramName: 'Country filter ids',
    queryKey: 'countries',
    shouldSendArray: true,
  },
  {
    paramName: 'Country filter titles',
    queryKey: 'countries',
    shouldMapIdsToTitles: true,
    shouldSendArray: true,
  },
]

const stringGuard = (value: null | string | undefined): value is string =>
  typeof value === 'string'

/**
 * Generate a map of filterId to its optionValueToTitleMap map. Looks like this:
 * ```typescript
 * {
 * "developer": { "dev-id-1": "Tracey", "dev-id-2": "Sendcloud" },
 * "sendcloud_plan": { "plan-id-1": "Premium", "plan-id-2": "Free" }
 * }
 * ```
 */
const generateFilterToOptionsMap = (filtersData: Filter[]) =>
  filtersData.reduce<Record<Filter['id'], OptionValueToTitleMap>>(
    (acc, filter) => {
      const currentFilterMap: OptionValueToTitleMap = {}
      filter.options.forEach((option) => {
        currentFilterMap[option.value] = option.title
      })
      acc[filter.id] = currentFilterMap
      return acc
    },
    {},
  )

/**
 * Parses query params using FILTER_PARSING_MAP and returns FilterAndSearchEventParams
 *
 * Do not be afraid of this function. Yes, it's a bit complicated,
 * because we decided to send filter titles along with filter ids.
 * So what happens is:
 * ```typescript
 * // we turn this queryObject:
 * { developer: ['some-id-1', 'some-id-2'] }}
 * // into this:
 * {'Developer filter titles': ['Tracey', 'Sendcloud']}`
 * ```
 */
export const getFiltersAndSearchEventParams = ({
  query,
  filtersData,
}: {
  query: LocationQuery
  filtersData: Filter[]
}): FilterAndSearchEventParams => {
  const filterToOptionsMap = generateFilterToOptionsMap(filtersData)

  const eventParams = FILTER_PARSING_MAP.reduce<FilterAndSearchEventParams>(
    (parsedParams, item) => {
      const { paramName, queryKey, shouldSendArray, shouldMapIdsToTitles } =
        item

      // rawValue (can be string[], or string)
      const rawValue = query[queryKey]

      if (!rawValue) {
        return parsedParams
      }

      /**
       * We need to turn our Ids into Titles, so we need a map for that.
       * Since queryKey === filter.id we can find our optionValueToTitleMap in filterToOptionsMap
       * optionValueToTitleMap will look this:
       * { "dev-id-1": "Tracey", "dev-id-2": "Sendcloud" }
       */
      const optionValueToTitleMap = shouldMapIdsToTitles
        ? filterToOptionsMap[queryKey]
        : undefined

      /**
       * Handle `string` rawValue (and mapping id to title, if needed)
       */
      if (typeof rawValue === 'string') {
        const formattedValue = shouldMapIdsToTitles
          ? optionValueToTitleMap?.[rawValue]
          : rawValue

        if (!formattedValue) {
          // nothing found in optionValueToTitleMap
          return parsedParams
        }
        // if the filter expects an array, we send array of 1 value
        parsedParams[paramName] = shouldSendArray
          ? [formattedValue]
          : formattedValue
        return parsedParams
      }

      /**
       * Handle `array` rawValue (and mapping ids to titles, if needed)
       */
      if (!shouldSendArray) {
        logger.error(
          new Error(
            `Tracking '${paramName}' expected string value, but received an array: ${rawValue.join()}`,
          ),
        )
        // if we get an unexpected array for single-value filter/query,
        // we log the last one, as that is the one that will be applied on BE
        const lastValue = rawValue[rawValue.length - 1] || undefined
        if (typeof lastValue === 'string') {
          parsedParams[paramName] = lastValue
        }
        return parsedParams
      }

      const formattedValues = shouldMapIdsToTitles
        ? rawValue.map(value => value && optionValueToTitleMap?.[value])
        : rawValue
      parsedParams[paramName] = formattedValues.filter(stringGuard)
      return parsedParams
    },
    {},
  )

  return eventParams
}
